/**
 * SEARCH PAGE - REIMAGINED
 *
 * Main property search page with natural language search,
 * map visualization, and featured listings.
 *
 * Features:
 * - Hero natural language search bar (centered, anchored)
 * - Interactive Mapbox map with property markers (right panel)
 * - Listings panel (left panel) with resizable split view
 * - Mobile-responsive layout with toggle views
 * - Advanced filters drawer with pull-down arrow
 *
 * @author Derrick J. Albers
 * @updated UX Team - Enhanced visual anchoring and search prominence
 */

"use client";

import React, { useState, useEffect, useRef, Suspense } from "react";
import dynamic from "next/dynamic";
import {
  getAllListingsForMap,
  type ListingSearchResult,
} from "@/app/actions/listing-search";

// Lazy load the heavy map component
const SearchClientMapbox = dynamic(() => import("./search-client-mapbox").then(mod => ({ default: mod.SearchClientMapbox })), {
  loading: () => (
    <div className="flex items-center justify-center h-full w-full bg-gray-100">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>
  ),
  ssr: false
});
import { getPropertyTypeCategories } from "@/app/actions/listing-search";
import type { ParsedQuery } from "@/types/natural-language-search";
import { RouteSelectionProvider } from "@/components/routing/route-selection-context";
import { Building2, ChevronDown, ChevronUp, SlidersHorizontal, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

/**
 * Search Page Component
 *
 * Displays a hero search bar and interactive map for property search.
 * Initial listings are loaded on mount for performance.
 */
function SearchPageComponent() {
  const [initialListings, setInitialListings] = useState<ListingSearchResult[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [parsedQuery, setParsedQuery] = useState<ParsedQuery | null>(null);
  const searchClientRef = useRef<any>(null);

  // Advanced filters state
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [categories, setCategories] = useState<Array<{id: string, name: string, displayName: string}>>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  // Load categories on mount
  useEffect(() => {
    async function loadCategories() {
      try {
        const cats = await getPropertyTypeCategories();
        setCategories(cats.filter(c => c.isActive));
      } catch (error) {
        console.error("Error loading categories:", error);
      }
    }
    loadCategories();
  }, []);

  // Load all listings on mount for US-wide map view
  useEffect(() => {
    async function loadListings() {
      try {
        const listings = await getAllListingsForMap();
        console.log(`Loaded ${listings.length} listings for search page map`);
        setInitialListings(listings);
      } catch (error) {
        console.error("Error loading listings:", error);
        setInitialListings([]);
      } finally {
        setIsLoading(false);
      }
    }
    loadListings();
  }, []);

  const handleSearch = (query: string, newParsedQuery: ParsedQuery) => {
    setSearchQuery(query);
    setParsedQuery(newParsedQuery);

    // Trigger search in SearchClientMapbox if it's mounted
    if (searchClientRef.current && searchClientRef.current.triggerSearch) {
      searchClientRef.current.triggerSearch(query, newParsedQuery);
    }
  };

  const toggleCategory = (categoryId: string) => {
    setSelectedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const clearFilters = () => {
    setSelectedCategories([]);
  };

  const applyFilters = () => {
    // Trigger search with selected categories via SearchClientMapbox
    if (searchClientRef.current && searchClientRef.current.applyCategoryFilters) {
      searchClientRef.current.applyCategoryFilters(selectedCategories);
    }
    setIsFiltersOpen(false);
  };

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center  bg-usland-white/50">
        <div className="text-center">
          <div className="mb-4 inline-flex items-center gap-3">
            <Building2 className="h-8 w-8 animate-pulse text-primary" />
            <span className="text-lg font-semibold text-foreground">
              Loading Properties...
            </span>
          </div>
          <div className="h-2 w-64 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full animate-pulse bg-primary"
              style={{ width: "60%" }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-usland-white/50">
      {/* Advanced Filters Drawer - Slides down from top */}
      <Sheet open={isFiltersOpen} onOpenChange={setIsFiltersOpen}>
        <SheetContent
          side="top"
          className="border-b border-gray-200 bg-white/95 backdrop-blur-sm dark:border-gray-800 dark:bg-gray-900/95 h-auto max-h-[60vh] overflow-y-auto rounded-b-2xl"
        >
          <SheetHeader className="border-b border-gray-200 pb-4 dark:border-gray-800">
            <div className="flex items-center justify-between">
              <SheetTitle className="flex items-center gap-2 text-xl font-bold">
                <SlidersHorizontal className="h-5 w-5 text-primary" />
                Advanced Filters
              </SheetTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                Clear All
              </Button>
            </div>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            {/* Property Type Categories */}
            <div>
              <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                Property Type
              </h3>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-1">
                {categories.map((category) => {
                  const isSelected = selectedCategories.includes(category.id);
                  return (
                    <button
                      key={category.id}
                      onClick={() => toggleCategory(category.id)}
                      className={cn(
                        "flex items-center gap-3 rounded-lg border p-3 text-left transition-all",
                        "hover:border-blue-400 hover:bg-blue-50 dark:hover:border-blue-600 dark:hover:bg-blue-950/30",
                        isSelected
                          ? "border-blue-500 bg-blue-50 dark:border-blue-500 dark:bg-blue-950/50"
                          : "border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
                      )}
                    >
                      <div
                        className={cn(
                          "flex h-5 w-5 items-center justify-center rounded border transition-colors",
                          isSelected
                            ? "border-blue-500 bg-blue-500"
                            : "border-gray-300 dark:border-gray-600"
                        )}
                      >
                        {isSelected && (
                          <div className="h-2 w-2 rounded-full bg-white" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {category.displayName}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Price Range Placeholder */}
            <div>
              <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                Price Range
              </h3>
              <div className="rounded-lg border border-dashed border-gray-300 p-4 text-center text-gray-400 dark:border-gray-700 dark:text-gray-600">
                Coming soon
              </div>
            </div>

            {/* Square Footage Placeholder */}
            <div>
              <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                Square Footage
              </h3>
              <div className="rounded-lg border border-dashed border-gray-300 p-4 text-center text-gray-400 dark:border-gray-700 dark:text-gray-600">
                Coming soon
              </div>
            </div>
          </div>

          {/* Action Buttons - Sticky at bottom of scrollable sheet */}
          <div className="sticky bottom-0 mt-6 border-t border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setIsFiltersOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={applyFilters}
                className="flex-1 bg-primary hover:bg-primary/90"
                disabled={selectedCategories.length === 0}
              >
                Apply Filters
                {selectedCategories.length > 0 && (
                  <span className="ml-2 rounded-full bg-white/20 px-2 py-0.5 text-xs">
                    {selectedCategories.length}
                  </span>
                )}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Filter Toggle Button - Fixed below header */}
      <div className="fixed left-1/2 top-16 z-[1035] -translate-x-1/2">
        <button
          onClick={() => setIsFiltersOpen(!isFiltersOpen)}
          className={cn(
            "flex items-center gap-2 rounded-b-2xl border-t border-x border-gray-300 bg-white/95 px-4 py-2 shadow-lg backdrop-blur-sm transition-all hover:bg-white dark:border-gray-700 dark:bg-gray-900/95 dark:hover:bg-gray-900",
            "hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0",
            isFiltersOpen && "rounded-b-none"
          )}
        >
          {isFiltersOpen ? (
            <>
              <ChevronUp className="h-4 w-4 text-gray-600 dark:text-gray-400" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Hide Filters
              </span>
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4 text-gray-600 dark:text-gray-400" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Advanced Filters
              </span>
              {selectedCategories.length > 0 && (
                <span className="ml-1 rounded-full bg-primary px-2 py-0.5 text-xs text-white">
                  {selectedCategories.length}
                </span>
              )}
            </>
          )}
        </button>
      </div>

      {/* Main Content - Split View with Anchored Panels */}
      <main className="h-0 flex-1">
        <Suspense fallback={
          <div className="flex items-center justify-center h-full w-full bg-gray-100">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        }>
          <SearchClientMapbox
            ref={searchClientRef}
            listings={initialListings}
            initialSearchQuery={searchQuery}
            initialParsedQuery={parsedQuery}
          />
        </Suspense>
      </main>
    </div>
  );
}

// Wrap SearchPageComponent with RouteSelectionProvider for route functionality
export default function SearchPage() {
  return (
    <RouteSelectionProvider>
      <SearchPageComponent />
    </RouteSelectionProvider>
  );
}
